import { Chart } from 'chart.js';
import {
    LIVE_CHART_COLORS as TEAM_COLORS,
    clamp,
    buildDataset,
    sharedOptions,
    createCustomLegend,
    updateCustomLegend,
    appendLegendItem
} from "./liveChartCore.js";

const MAX_POINTS = 500;

const getCollapsedCookie = () => {
    const match = document.cookie.match('(^|[^;]+)\\s*liveChartCollapsed\\s*=\\s*([^;]+)');
    return match ? match.pop() === '1' : false;
};

const setCollapsedCookie = (collapsed) => {
    const date = new Date();
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
    document.cookie = `liveChartCollapsed=${collapsed ? '1' : '0'}; expires=${date.toUTCString()}; path=/`;
};

let liveTournamentState = null;

const readNumber = (selector, fallback) => {
    const element = document.querySelector(selector);
    if (!element) return fallback;

    const raw = element.value || element.textContent || element.getAttribute('data-rate');
    const value = Number.parseFloat(raw);

    return Number.isFinite(value) ? value : fallback;
};

const getTeamNames = () => {
    const titleItems = document.querySelectorAll('.teams-match-info > .teams-match__item:not(.center) .teams-match__item-title');
    const names = Array.from(titleItems).map(item => item.textContent.trim()).filter(Boolean);

    return [
        names[0] || 'Team 1',
        names[1] || 'Team 2'
    ];
};

const getLiveTypes = () => {
    const items = document.querySelectorAll('.teams-match-info .bet-item[data-type*="live"]');
    const types = Array.from(items).map(item => item.getAttribute('data-type')).filter(Boolean);

    return [...new Set(types)];
};

const isLiveType = type => Boolean(type && type.includes('live'));

const getActiveLiveBetItem = () => document.querySelector('.teams-match-info .bet-item.active[data-type*="live"]');

const getActiveLiveType = () => {
    return getActiveLiveBetItem()?.getAttribute('data-type') || null;
};

const getTypeTitle = type => {
    if (!type) return 'Live bet';

    const item = Array.from(document.querySelectorAll('.teams-match-info .bet-item[data-type]'))
        .find(element => element.getAttribute('data-type') === type);

    return item?.getAttribute('data-type_title') || type.replace(/_/g, ' ');
};

const getTypeIcon = (type) => {
    const item = document.querySelector(`.teams-match-info .bet-item[data-type="${type}"]`);
    return item?.getAttribute('data-type_icon') || '/template/images/svg/graphic.svg';
};

const getStartRates = type => {
    let team1 = readNumber(`span.js_${type}_team_1_rate`, 50);
    let team2 = readNumber(`span.js_${type}_team_2_rate`, 100 - team1);

    if (!Number.isFinite(team1) || !Number.isFinite(team2)) {
        team1 = 50;
        team2 = 50;
    }

    if (team1 + team2 <= 0) {
        team1 = 50;
        team2 = 50;
    }

    const total = team1 + team2;
    team1 = Math.round((team1 / total) * 100);
    team2 = 100 - team1;

    return [clamp(team1, 1, 99), clamp(team2, 1, 99)];
};

// makeLabel is no longer needed - timestamps come from API

const createFallbackHistory = (type) => {
    const start = getStartRates(type);
    const now = new Date();
    return {
        labels: [now.toLocaleTimeString([], {
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
        })],
        values: [[start[0]], [start[1]]]
    };
};

const fetchHistory = async (type) => {
    const tournamentId = document.getElementById('js_tournament_id')?.value;
    if (!tournamentId || !type) return createFallbackHistory(type);

    // Convert UI type to API bet_type
    // UI types include "live_" prefix, API uses same format
    const betType = type;

    try {
        const url = `https://${window.location.host}/widget/cofs_api.php?tournament_id=${tournamentId}&bet_type=${encodeURIComponent(betType)}&history=1`;
        const response = await fetch(url);
        const data = await response.json();
        const raw = data?.response;
        const points = Array.isArray(raw) ? raw : Array.isArray(raw?.cofs) ? raw.cofs : null;

        if (!points?.length || points[0].timestamp == null) {
            return createFallbackHistory(type);
        }

        const labels = [], team1 = [], team2 = [];
        points.forEach(point => {
            labels.push(new Date(point.timestamp * 1000).toLocaleTimeString([], {
                hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
            }));
            team1.push(point.team_1_rate);
            team2.push(point.team_2_rate);
        });

        return { labels, values: [team1, team2] };
    } catch (error) {
        console.error('Failed to fetch chart history:', error);
        return createFallbackHistory(type);
    }
};

const getLatestValues = state => {
    const latest1 = state.history.values[0][state.history.values[0].length - 1];
    const latest2 = state.history.values[1][state.history.values[1].length - 1];

    return [latest1, latest2];
};

const getPreviousValues = state => {
    const values = getLatestValues(state);
    const prev1 = state.history.values[0][state.history.values[0].length - 2] || values[0];
    const prev2 = state.history.values[1][state.history.values[1].length - 2] || values[1];

    return [prev1, prev2];
};

const formatDelta = value => `${value >= 0 ? '+' : ''}${value}%`;

const createChart = (canvas, variant, state) => {
    const ctx = canvas.getContext('2d');
    const data = state.history;
    const teamNames = state.teamNames;

    const variantConfig = {
        fill: false,
        legend: true,
        grid: true,
        ticks: true,
        pointRadius: 0,
        tension: 0.4,
        borderWidth: 2,
        cubicInterpolationMode: 'monotone',
        yPosition: 'right',
        xTickLimit: 4,
        yTickLimit: 5,
        xGridColor: 'rgba(93, 120, 140, 0.08)',
        yGridColor: 'rgba(93, 120, 140, 0.12)',
        layoutPadding: {right: 12, top: 24, bottom: 8}
    };

    return new Chart(ctx, {
        type: 'line',
        data: {
            labels: data.labels,
            datasets: [
                buildDataset(data.values[0], teamNames[0], TEAM_COLORS[0], variantConfig),
                buildDataset(data.values[1], teamNames[1], TEAM_COLORS[1], variantConfig)
            ]
        },
        options: sharedOptions({
            legend: variantConfig.legend,
            grid: variantConfig.grid,
            ticks: variantConfig.ticks,
            min: 0,
            max: 100,
            yPosition: variantConfig.yPosition,
            xTickLimit: variantConfig.xTickLimit,
            yTickLimit: variantConfig.yTickLimit,
            xGridColor: variantConfig.xGridColor,
            yGridColor: variantConfig.yGridColor,
            layoutPadding: variantConfig.layoutPadding
        })
    });
};

const createCanvas = (parent, variant, className = 'live-tournament-chart__canvas') => {
    const wrap = document.createElement('div');
    wrap.className = className;
    const canvas = document.createElement('canvas');
    canvas.setAttribute('data-live-chart-variant', variant);
    wrap.appendChild(canvas);
    parent.appendChild(wrap);

    return canvas;
};


const updateHeader = state => {
    const title = state.root.querySelector('[data-live-chart-title]');
    if (title) title.textContent = `Live win %: ${getTypeTitle(state.type)}`;

    const current = state.root.querySelector('[data-live-chart-current]');
    if (current) {
        const [latest1, latest2] = getLatestValues(state);
        current.textContent = `${state.teamNames[0]} ${latest1}% / ${state.teamNames[1]} ${latest2}%`;
    }

    const typeLabel = state.root.querySelector('[data-live-chart-type-label]');
    if (typeLabel) {
        typeLabel.textContent = getTypeTitle(state.type);
    }
};

const updateTape = state => {
    // No-op: polymarket-multi has no tape elements
};

const updateVariantStats = state => {
    // No-op: polymarket-multi has no DOM stat elements (all canvas-rendered)
};

const renderCharts = state => {
    state.charts.forEach(chart => chart.destroy());
    state.charts = [];

    state.root.querySelectorAll('canvas[data-live-chart-variant]').forEach(canvas => {
        const chart = createChart(canvas, canvas.getAttribute('data-live-chart-variant'), state);
        state.charts.push(chart);
        // Initialize custom legend
        updateCustomLegend(chart, null);
    });

    updateHeader(state);
    updateTape(state);
    updateVariantStats(state);
};

const createMarkup = state => {
    const root = document.createElement('div');
    root.className = 'live-tournament-chart';

    // Content wrapper
    const content = document.createElement('div');
    content.className = 'live-tournament-chart__content';

    // Top bar (legend + toggle button in one row)
    const topBar = document.createElement('div');
    topBar.className = 'live-tournament-chart__top-bar';

    const chartIcon = document.createElement('img');
    chartIcon.className = 'live-tournament-chart__icon';
    chartIcon.src = '/template/images/svg/graphic.svg';
    chartIcon.alt = '';
    topBar.appendChild(chartIcon);

    createCustomLegend(topBar);

    const typeWrapper = document.createElement('span');
    typeWrapper.className = 'live-tournament-chart__type-wrapper';

    const typeIcon = document.createElement('img');
    typeIcon.className = 'live-tournament-chart__type-icon';
    typeIcon.setAttribute('data-live-chart-type-icon', '');
    typeIcon.alt = '';

    const typeLabel = document.createElement('span');
    typeLabel.className = 'live-tournament-chart__type-label';
    typeLabel.setAttribute('data-live-chart-type-label', '');

    typeWrapper.append(typeIcon, typeLabel);
    topBar.appendChild(typeWrapper);

    const toggleIcon = document.createElement('span');
    toggleIcon.className = 'toggle-arrow';
    toggleIcon.setAttribute('aria-hidden', 'true');

    topBar.appendChild(toggleIcon);
    content.appendChild(topBar);

    createCanvas(content, 'polymarket-multi');
    root.appendChild(content);

    // Restore collapsed state from cookie
    const collapsed = getCollapsedCookie();
    root.classList.toggle('is-collapsed', collapsed);

    // Toggle handler - entire top-bar is clickable
    topBar.addEventListener('click', () => {
        const isNowCollapsed = root.classList.toggle('is-collapsed');
        setCollapsedCookie(isNowCollapsed);

        if (!isNowCollapsed) {
            setTimeout(() => state.charts.forEach(chart => chart.resize()), 50);
        }
    });

    return root;
};

const handleCofsUpdate = (state) => (event) => {
    const cofs = event.detail?.cofs;
    if (!cofs || !state.type) return;

    const cof = cofs.find(c => c.type === state.type);
    if (!cof) return;

    const team1Rate = Math.round(cof.team_1_rate);
    const team2Rate = Math.round(cof.team_2_rate);

    state.history.labels.push(new Date().toLocaleTimeString([], {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }));
    state.history.values[0].push(team1Rate);
    state.history.values[1].push(team2Rate);

    while (state.history.labels.length > MAX_POINTS) {
        state.history.labels.shift();
        state.history.values[0].shift();
        state.history.values[1].shift();
    }

    state.charts.forEach(chart => {
        chart.data.labels = state.history.labels;
        chart.data.datasets.forEach((dataset, index) => {
            dataset.data = state.history.values[index];
        });
        chart.update('none');
        updateCustomLegend(chart, chart.$hoverIndex);
    });

    updateHeader(state);
};

let fetchGeneration = 0;

const setType = async (state, type) => {
    if (!type || type === state.type) return;
    state.type = type;
    state.root.classList.add('is-loading');

    // Fill legend with current DOM data during loading
    const legend = state.root.querySelector('.live-tournament-chart__custom-legend');
    if (legend) {
        const names = getTeamNames();
        const rates = getStartRates(type);
        legend.innerHTML = '';
        names.forEach((name, i) => {
            appendLegendItem(legend, TEAM_COLORS[i], `${rates[i]}%`, name);
        });
    }

    const typeLabel = state.root.querySelector('[data-live-chart-type-label]');
    if (typeLabel) typeLabel.textContent = getTypeTitle(type);

    const iconEl = state.root.querySelector('[data-live-chart-type-icon]');
    if (iconEl) iconEl.src = getTypeIcon(type);

    const thisGeneration = ++fetchGeneration;
    const history = await fetchHistory(type);

    if (thisGeneration !== fetchGeneration) return;

    state.history = history;
    state.root.classList.remove('is-loading');
    renderCharts(state);
};

const syncVisibility = async (state) => {
    const activeType = getActiveLiveType();
    const shouldShow = isLiveType(activeType);

    state.root.classList.toggle('is-hidden', !shouldShow);

    if (shouldShow) {
        await setType(state, activeType);
        setTimeout(() => state.charts.forEach(chart => chart.resize()), 0);
    } else {
        state.type = null;
    }
};

export const chartLiveTournament = async () => {
    if (liveTournamentState) return liveTournamentState;

    const match = document.querySelector('.teams-match-info');
    const target = document.getElementById('tournament__notification_block_2') || document.querySelector('.total-check-grid');
    const type = getActiveLiveType();
    const initialType = type || getLiveTypes()[0];

    if (!match || !target || !initialType) return false;

    const state = {
        type: null,
        teamNames: getTeamNames(),
        history: await fetchHistory(initialType),
        charts: [],
        root: null
    };
    state.type = initialType;

    state.root = createMarkup(state);
    target.parentNode.insertBefore(state.root, target);

    const iconEl = state.root.querySelector('[data-live-chart-type-icon]');
    if (iconEl) iconEl.src = getTypeIcon(state.type);

    renderCharts(state);
    await syncVisibility(state);

    // Register event listener for live updates
    document.addEventListener('live-cofs-updated', handleCofsUpdate(state));

    document.addEventListener('click', async (event) => {
        const item = event.target.closest?.('.teams-match-info .bet-item[data-type]');
        if (!item) return;

        await syncVisibility(state);
    });

    liveTournamentState = state;

    return state;
};
